
import { supabase } from '@/integrations/supabase/client';
import { FilterOptions, Plan, SortOption } from '../types';

// Convert database record to Plan type
const mapToPlan = (record: any): Plan => {
  return {
    ...record,
    features: Array.isArray(record.features) 
      ? record.features 
      : (typeof record.features === 'string' 
          ? JSON.parse(record.features) 
          : [])
  };
};

// Fetch featured plans
export const getFeaturedPlans = async (): Promise<Plan[]> => {
  try {
    const { data, error } = await supabase
      .from('plans')
      .select('*')
      .eq('featured', true)
      .order('popularity', { ascending: false });
    
    if (error) {
      console.error('Error fetching featured plans:', error);
      throw error;
    }
    
    // Map response to Plan type
    return (data || []).map(mapToPlan);
  } catch (error) {
    console.error('Error in getFeaturedPlans:', error);
    throw error;
  }
};

export const fetchFilteredPlans = async (
  filters: FilterOptions,
  sortBy: SortOption = 'relevance',
  searchQuery: string = ''
): Promise<Plan[]> => {
  let query = supabase.from('plans').select('*');

  // Apply provider filter
  if (filters.providers && filters.providers.length > 0) {
    query = query.in('provider', filters.providers);
  }

  // Apply data allowance filter
  if (filters.dataAllowances && filters.dataAllowances.length > 0) {
    query = query.in('data_allowance', filters.dataAllowances);
  }

  // Apply contract length filter
  if (filters.contractLengths && filters.contractLengths.length > 0) {
    query = query.in('contract_length', filters.contractLengths);
  }
  
  // Apply talk time filter
  if (filters.talkTimes && filters.talkTimes.length > 0) {
    query = query.in('minutes', filters.talkTimes);
  }

  // Apply roaming filter
  if (filters.roaming && filters.roaming.length > 0) {
    query = query.in('roaming', filters.roaming);
  }

  // Apply 5G filter
  if (filters.has5G !== undefined) {
    query = query.eq('has_5g', filters.has5G);
  }

  // Apply search query filter if provided
  if (searchQuery && searchQuery.trim() !== '') {
    const lowerQuery = searchQuery.toLowerCase();
    
    if (lowerQuery === "ubegrænset tale") {
      // Search for plans with unlimited talk time
      query = query.eq('minutes', 'Ubegrænset');
    } else if (lowerQuery === "ubegrænset data") {
      // Search for plans with unlimited data
      query = query.eq('data_allowance', 'Ubegrænset');
    } else if (lowerQuery === "streaming" || lowerQuery === "streamingtjenester") {
      // Search for plans that mention streaming services in features or description
      query = query.or(`features.cs.["Viaplay"],features.cs.["TV2 Play"],features.cs.["Netflix"],features.cs.["Spotify"],features.cs.["YouTube Music"],description.ilike.%streaming%,description.ilike.%viaplay%,description.ilike.%tv2%,description.ilike.%netflix%,description.ilike.%spotify%,description.ilike.%lydbøger%`);
    } else if (lowerQuery === "fri data" || lowerQuery === "fri") {
      // For "fri data" or "fri", search for plans with "Ubegrænset" data allowance
      query = query.eq('data_allowance', 'Ubegrænset');
    } else {
      // For other search terms, apply normal search
      const searchTerms = lowerQuery.split(/\s+/);
      
      // Apply search filter for each term
      searchTerms.forEach(term => {
        const searchPattern = `%${term}%`;
        query = query.or(`name.ilike.${searchPattern},provider.ilike.${searchPattern},description.ilike.${searchPattern},data_allowance.ilike.${searchPattern}`);
      });
    }
  }

  // Apply sorting
  switch (sortBy) {
    case 'priceAsc':
      query = query.order('price', { ascending: true });
      break;
    case 'priceDesc':
      query = query.order('price', { ascending: false });
      break;
    case 'dataAsc':
      // Data sorting is complex and done client-side
      break;
    case 'dataDesc':
      // Data sorting is complex and done client-side
      break;
    case 'ratingDesc':
      query = query.order('rating', { ascending: false });
      break;
    case 'popularityDesc':
      query = query.order('popularity', { ascending: false });
      break;
    case 'relevance':
    default:
      // Default sorting by relevance - featured plans first, then by popularity
      query = query.order('featured', { ascending: false }).order('popularity', { ascending: false });
      break;
  }

  try {
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }

    // Client-side sorting for data when needed
    let plans = data ? data.map(mapToPlan) : [];
    
    if (sortBy === 'dataAsc' || sortBy === 'dataDesc') {
      plans = [...plans].sort((a, b) => {
        const getDataValue = (dataStr: string): number => {
          if (dataStr === 'Ubegrænset') return Infinity;
          const match = dataStr.match(/(\d+)\s*(GB|MB)/i);
          if (!match) return 0;
          const value = parseInt(match[1]);
          return match[2].toLowerCase() === 'mb' ? value / 1024 : value; // Convert MB to GB
        };
        
        const aValue = getDataValue(a.data_allowance);
        const bValue = getDataValue(b.data_allowance);
        
        return sortBy === 'dataAsc' ? aValue - bValue : bValue - aValue;
      });
    }

    return plans;
  } catch (error) {
    console.error('Error in fetchFilteredPlans:', error);
    throw error;
  }
};

// Seed the database with initial plans
export const seedInitialPlans = async (plans: any[]): Promise<void> => {
  try {
    const { error } = await supabase.from('plans').insert(plans);
    
    if (error) {
      console.error('Error seeding plans:', error);
      throw error;
    }
    
    console.log('Successfully seeded plans');
  } catch (error) {
    console.error('Error in seedInitialPlans:', error);
    throw error;
  }
};
